/**
 * Masse salariale et salaires bruts individuels.
 *
 * Les charges de personnel représentent 71 % du budget : elles ne peuvent pas
 * être pilotées par un taux d'évolution global. Ce module les calcule salarié
 * par salarié, mois par mois, à partir de la grille réelle tenue dans
 * `paie_evolution_salaires`, qui porte le brut individuel de juillet 2008 à
 * décembre 2030.
 *
 * Le taux de charges patronales est **constaté** sur les écritures de paie,
 * jamais postulé. À défaut d'écritures, un taux de repli déclaré en
 * configuration prend le relais et le résultat porte la mention `estime`.
 *
 * Ce module ne calcule pas la paie : `scc-paie` produit les bulletins, celui-ci
 * les lit pour piloter la masse.
 */
import { DECISIONS_REMUNERATION, PRIME_PARTAGE_VALEUR, TAUX_CHARGES_PATRONALES_REPLI, } from './configuration.js';
import { sommeExacte } from './adherents.js';
/** Nom d'affichage d'un salarié : « Prénom NOM ». */
export function nomAffiche(s) {
    return `${s.prenom} ${s.nom}`.trim();
}
/**
 * Une écriture pèse-t-elle sur le résultat ?
 *
 * Seuls les comptes de classe 6 sont des charges. Les comptes de classe 4 qui
 * accompagnent une écriture de paie — 431 URSSAF, 437 autres organismes, 421
 * personnel — sont des comptes de tiers : ils enregistrent une dette, pas une
 * charge. Les confondre donne un taux de charges patronales négatif.
 */
export function estCompteDeCharge(comptePcg) {
    return comptePcg.startsWith('6');
}
/** Impact d'une écriture sur le résultat : positif pour une charge. */
export function impactCharge(e) {
    return estCompteDeCharge(e.comptePcg) ? e.debit - e.credit : 0;
}
/**
 * Taux de charges patronales constaté : cotisations patronales rapportées au
 * brut, sur les seuls comptes de charge.
 */
export function tauxChargesPatronales(ecritures) {
    const brut = sommeExacte(ecritures.filter((e) => e.nature === 'BRUT').map(impactCharge));
    const cot = sommeExacte(ecritures.filter((e) => e.nature === 'COT_PAT').map(impactCharge));
    const mois = new Set(ecritures.map((e) => e.mois)).size;
    // Un brut nul ou négatif rend le rapport insignifiant : on retombe sur le
    // taux de repli plutôt que de produire une valeur absurde.
    if (brut <= 0 || cot <= 0) {
        return {
            taux: TAUX_CHARGES_PATRONALES_REPLI,
            constate: false,
            brutObserve: brut,
            cotisationsObservees: cot,
            moisObserves: mois,
        };
    }
    return {
        taux: cot / brut,
        constate: true,
        brutObserve: brut,
        cotisationsObservees: cot,
        moisObserves: mois,
    };
}
/** Brut annuel d'un salarié : somme de ses douze points mensuels. */
export function brutAnnuel(points, salarie, exercice) {
    const prefixe = String(exercice);
    const mois = points.filter((p) => p.salarie === salarie && p.date.startsWith(prefixe));
    return {
        brut: sommeExacte(mois.map((p) => p.brutMensuel)),
        moisRenseignes: mois.length,
    };
}
/** Masse salariale d'un exercice. */
export function masseSalariale(e, exercice) {
    const { taux, constate } = tauxChargesPatronales(e.ecritures);
    const situations = [];
    for (const s of e.salaries) {
        const { brut, moisRenseignes } = brutAnnuel(e.points, s.id, exercice);
        if (moisRenseignes === 0)
            continue;
        const moisExercice = e.points
            .filter((p) => p.salarie === s.id && p.date.startsWith(String(exercice)))
            .sort((a, b) => a.date.localeCompare(b.date));
        const dernier = moisExercice[moisExercice.length - 1];
        const precedent = brutAnnuel(e.points, s.id, exercice - 1);
        // Seule la fraction de prime excédant le plafond supporte les charges.
        const pr = ventilerPrimes(e.primes ?? [], s.id, exercice);
        const charges = Math.round((brut + pr.chargee) * taux * 100) / 100;
        situations.push({
            salarie: s,
            exercice,
            brutAnnuel: brut,
            brutMensuelMoyen: moisRenseignes > 0 ? brut / moisRenseignes : 0,
            brutMensuelFin: dernier?.brutMensuel ?? 0,
            moisRenseignes,
            groupe: dernier?.groupe ?? null,
            echelon: dernier?.echelon ?? null,
            chargesPatronales: charges,
            primes: { total: pr.total, exoneree: pr.exoneree, chargee: pr.chargee, nombre: pr.nombre },
            coutEmployeur: sommeExacte([brut, charges, pr.total]),
            progression: precedent.moisRenseignes > 0 && precedent.brut > 0 ? brut / precedent.brut - 1 : null,
        });
    }
    const brut = sommeExacte(situations.map((s) => s.brutAnnuel));
    const chargesPatronales = sommeExacte(situations.map((s) => s.chargesPatronales));
    const tr = e.titresRestaurantParExercice?.[exercice] ?? 0;
    const primes = sommeExacte(situations.map((s) => s.primes.total));
    return {
        exercice,
        brut,
        chargesPatronales,
        titresRestaurant: tr,
        primes,
        masseChargee: sommeExacte([brut, chargesPatronales, tr, primes]),
        taux,
        tauxConstate: constate,
        effectif: situations.length,
        situations: situations.sort((a, b) => b.brutAnnuel - a.brutAnnuel),
        exerciceIncomplet: situations.some((s) => s.moisRenseignes < 12),
    };
}
/** Série de masses salariales sur une plage d'exercices. */
export function serieMasseSalariale(e, premier, dernier) {
    const out = [];
    for (let a = premier; a <= dernier; a++) {
        const m = masseSalariale(e, a);
        if (m.effectif > 0)
            out.push(m);
    }
    return out;
}
/** Historique complet du brut d'un salarié, un point par mois. */
export function historiqueSalarie(e, salarie) {
    return e.points
        .filter((p) => p.salarie === salarie)
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((p) => ({
        date: p.date,
        brutMensuel: p.brutMensuel,
        groupe: p.groupe,
        echelon: p.echelon,
        motif: p.motif,
    }));
}
/**
 * Ventilation des écritures de paie par nature, pour rapprocher la masse
 * calculée des écritures constatées.
 *
 * `charge` ne retient que les comptes de classe 6 ; `tiers` porte les
 * mouvements de dette, qui équilibrent l'écriture sans peser sur le résultat.
 */
export function ventilationPaie(ecritures, exercice) {
    const prefixe = String(exercice);
    const m = new Map();
    for (const e of ecritures) {
        if (!e.mois.startsWith(prefixe))
            continue;
        const cur = m.get(e.nature) ?? { charge: 0, tiers: 0, n: 0 };
        if (estCompteDeCharge(e.comptePcg))
            cur.charge = sommeExacte([cur.charge, e.debit - e.credit]);
        else
            cur.tiers = sommeExacte([cur.tiers, e.credit - e.debit]);
        cur.n += 1;
        m.set(e.nature, cur);
    }
    return [...m.entries()]
        .map(([nature, v]) => ({ nature, charge: v.charge, tiers: v.tiers, nbEcritures: v.n }))
        .sort((a, b) => Math.abs(b.charge) - Math.abs(a.charge));
}
/**
 * Ventilation d'une prime entre part exonérée et part chargée.
 *
 * La prime de partage de la valeur est exonérée de cotisations dans la limite
 * annuelle par bénéficiaire ; au-delà, la fraction excédentaire est traitée
 * comme du salaire. Le plafond s'apprécie sur le cumul de l'exercice, pas
 * prime par prime.
 */
export function ventilerPrimes(primes, salarie, exercice) {
    const p = PRIME_PARTAGE_VALEUR;
    const plafond = p.accordInteressement ? p.plafondExonerationRenforce : p.plafondExoneration;
    const retenues = primes.filter((x) => x.salarie === salarie && x.moisVersement.startsWith(String(exercice)));
    const total = sommeExacte(retenues.map((x) => x.montantBrut));
    const eligibles = sommeExacte(retenues.filter((x) => x.type === 'PPV' && x.exonerationPresumee).map((x) => x.montantBrut));
    const exoneree = Math.min(eligibles, plafond);
    return {
        total,
        exoneree,
        chargee: sommeExacte([total, -exoneree]),
        plafond,
        nombre: retenues.length,
    };
}
/**
 * Trajectoires salariales prédéfinies.
 *
 * La grille tenue dans la base porte déjà une projection jusqu'en 2030 : elle
 * sert de trajectoire de référence. Les autres se calculent depuis le dernier
 * exercice réel, en appliquant une revalorisation générale annuelle.
 */
export const TRAJECTOIRES_SALARIALES = [
    {
        cle: 'grille',
        libelle: 'Grille en l’état',
        description: 'La trajectoire telle qu’elle est tenue dans la base de paie, échelons et anciennetés compris.',
        revalorisationAnnuelle: null,
    },
    {
        cle: 'gel',
        libelle: 'Gel des salaires',
        description: 'Aucune revalorisation : le brut reste au niveau du dernier exercice réel.',
        revalorisationAnnuelle: 0,
    },
    {
        cle: 'nao_minimale',
        libelle: 'NAO minimale',
        description: 'Revalorisation générale de 1 % par an, sans mesure individuelle.',
        revalorisationAnnuelle: 0.01,
    },
    {
        cle: 'pouvoir_achat',
        libelle: 'Maintien du pouvoir d’achat',
        description: 'Revalorisation générale alignée sur l’inflation retenue, soit 2,5 % par an.',
        revalorisationAnnuelle: 0.025,
    },
    {
        cle: DECISIONS_REMUNERATION.cle,
        libelle: DECISIONS_REMUNERATION.libelle,
        description: 'Marielle Pautonnier atteint 2 800 € bruts mensuels au 1ᵉʳ janvier 2030 ; Ariane Pouget est ' +
            'augmentée de la moitié de ce taux, Yannis Jean de la moitié du sien. Les taux se déduisent ' +
            'de la cible et de la grille réelle, ils ne sont pas saisis.',
        // Le taux n'est pas unique : il se calcule salarié par salarié.
        revalorisationAnnuelle: 0,
    },
];
/**
 * Taux annuels déduits des décisions du bureau, salarié par salarié.
 *
 * La décision ne fixe pas trois taux mais une cible et une chaîne de
 * proportions. Le taux du référent est celui qui, appliqué chaque année depuis
 * son brut de départ, l'amène exactement à la cible ; les autres en sont des
 * fractions déclarées en configuration.
 *
 * La cible est un **minimum** : si le brut de départ la dépasse déjà, le taux
 * est nul plutôt que négatif — une décision d'augmentation ne fait pas baisser
 * un salaire.
 */
/**
 * Chaîne de bruts obtenue en appliquant un taux, arrondie à la centime chaque
 * exercice — exactement ce que fait `projeterGrille`.
 */
function brutApresPas(depart, taux, nbPas) {
    let v = depart;
    for (let i = 0; i < nbPas; i++)
        v = Math.round(v * (1 + taux) * 100) / 100;
    return v;
}
/**
 * Plus petit taux annuel dont la chaîne **arrondie** atteint la cible.
 *
 * Le taux analytique `(cible / départ)^(1/n) − 1` fait manquer la cible d'un
 * centime : chaque exercice arrondit à la baisse et l'écart se cumule. La cible
 * étant un **minimum**, on cherche par dichotomie le plus petit taux qui la
 * franchit une fois les arrondis appliqués. Vingt itérations suffisent à fixer
 * le taux au milliardième, et le résultat est déterministe.
 */
export function tauxAtteignantLaCible(depart, cible, nbPas) {
    const analytique = (cible / depart) ** (1 / nbPas) - 1;
    if (analytique <= 0)
        return 0;
    if (brutApresPas(depart, analytique, nbPas) >= cible)
        return analytique;
    let bas = analytique;
    // La borne haute reste très proche : il ne manque que des centimes.
    let haut = analytique + 0.0001;
    for (let i = 0; i < 20; i++) {
        const milieu = (bas + haut) / 2;
        if (brutApresPas(depart, milieu, nbPas) >= cible)
            haut = milieu;
        else
            bas = milieu;
    }
    return haut;
}
export function tauxDecisionsBureau(points, salaries, exerciceBase) {
    const d = DECISIONS_REMUNERATION;
    const out = new Map();
    /** Dernier brut mensuel connu de l'exercice de base. */
    const brutDepart = (id) => {
        const mois = points
            .filter((p) => p.salarie === id && p.date.startsWith(String(exerciceBase)))
            .sort((a, b) => a.date.localeCompare(b.date));
        return mois[mois.length - 1]?.brutMensuel ?? 0;
    };
    const referent = salaries.find((s) => s.nom.toUpperCase() === d.cible.nom);
    const depart = referent ? brutDepart(referent.id) : 0;
    const nbPas = d.cible.exercice - exerciceBase;
    const tauxReferent = depart > 0 && nbPas > 0
        ? tauxAtteignantLaCible(depart, d.cible.brutMensuel, nbPas)
        : 0;
    for (const s of salaries) {
        const part = d.parts[s.nom.toUpperCase()] ?? d.partParDefaut;
        out.set(s.id, tauxReferent * part);
    }
    return out;
}
/**
 * Projette la grille de salaires sur une trajectoire.
 *
 * Le premier exercice est repris tel quel : c'est le réel. Chaque exercice
 * suivant part du brut du dernier mois de l'exercice précédent, lui applique
 * la revalorisation puis la mesure individuelle éventuelle.
 *
 * La trajectoire `grille` rend les points d'origine sans les toucher : la base
 * de paie porte déjà sa propre projection, échelons compris, et la recalculer
 * l'écraserait.
 */
export function projeterGrille(points, salaries, exerciceBase, exercices, trajectoire, hypotheses = []) {
    const modele = TRAJECTOIRES_SALARIALES.find((t) => t.cle === trajectoire);
    if (!modele || modele.revalorisationAnnuelle === null)
        return [...points];
    // Les décisions du bureau n'ont pas un taux mais un taux par salarié, déduit
    // d'une cible : il se calcule ici et prime sur la revalorisation du modèle.
    const tauxIndividuels = trajectoire === DECISIONS_REMUNERATION.cle
        ? tauxDecisionsBureau(points, salaries, exerciceBase)
        : null;
    // Les exercices antérieurs à la base sont du réel : ils ne bougent pas.
    const conserves = points.filter((p) => Number(p.date.slice(0, 4)) <= exerciceBase);
    const projetes = [];
    for (const s of salaries) {
        const moisBase = conserves
            .filter((p) => p.salarie === s.id && p.date.startsWith(String(exerciceBase)))
            .sort((a, b) => a.date.localeCompare(b.date));
        const depart = moisBase[moisBase.length - 1];
        if (!depart)
            continue;
        let reference = depart;
        for (const annee of exercices.filter((a) => a > exerciceBase)) {
            const h = hypotheses.find((x) => x.trajectoire === trajectoire &&
                x.exercice === annee &&
                (x.salarie === s.id || x.salarie === '*'));
            // Une hypothèse arbitrée prime sur toute trajectoire ; à défaut, le taux
            // individuel des décisions du bureau prime sur celui du modèle.
            const taux = h
                ? h.revalorisation
                : (tauxIndividuels?.get(s.id) ?? modele.revalorisationAnnuelle);
            const mesure = h?.mesureIndividuelle ?? 0;
            const brut = Math.round((reference.brutMensuel * (1 + taux) + mesure) * 100) / 100;
            const dernierMois = h?.dernierMois ?? 12;
            for (let m = 1; m <= dernierMois; m++) {
                projetes.push({
                    salarie: s.id,
                    date: `${annee}-${String(m).padStart(2, '0')}-01`,
                    brutMensuel: brut,
                    groupe: reference.groupe,
                    echelon: reference.echelon,
                    motif: h ? 'hypothèse' : modele.libelle,
                });
            }
            reference = {
                salarie: s.id,
                date: `${annee}-12-01`,
                brutMensuel: brut,
                groupe: reference.groupe,
                echelon: reference.echelon,
                motif: null,
            };
        }
    }
    return [...conserves, ...projetes];
}
/**
 * Salaire mensuel de référence servant au calcul d'une indemnité de rupture :
 * moyenne des douze derniers mois de l'exercice, primes de l'exercice
 * incluses au douzième.
 */
export function salaireReferenceRupture(points, primes, salarie, exercice) {
    const { brut, moisRenseignes } = brutAnnuel(points, salarie, exercice);
    if (moisRenseignes === 0)
        return 0;
    const p = ventilerPrimes(primes, salarie, exercice);
    return Math.round(((brut + p.total) / moisRenseignes) * 100) / 100;
}
//# sourceMappingURL=paie.js.map